<script setup>
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePipelinesStore } from 'dashboard/stores/pipelines';
import FlowSelect from './FlowSelect.vue';
import FlowGroupedSelect from './FlowGroupedSelect.vue';
import FlowActionBody from './FlowActionBody.vue';
import MessageEditorModal from './MessageEditorModal.vue';
import {
  getActionGroups,
  initialActionData,
  isPixelAction,
  isRecordAction,
  isWaitAction,
} from './actionCatalog';
import {
  decodeField,
  encodeField,
  findField,
  newRule,
  operatorNeedsValue,
} from './conditionFields';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import { isMetaAction } from './metaConversion';
import {
  NODE_WIDTH,
  HEADER_HEIGHT,
  FIELD_HEIGHT,
  BODY_PADDING,
  FIELD_GAP,
  BRANCH_HEIGHT,
  STAGES_BOX_HEIGHT,
  isDealEvent,
  TRIGGER_EVENTS,
  actionNeedsValue,
  nodeHeight,
} from './flowHelper';

const props = defineProps({
  node: { type: Object, required: true },
  triggerEvent: { type: String, default: '' },
  errors: { type: Array, default: () => [] },
  fieldGroups: { type: Array, default: () => [] },
  actionOptions: { type: Function, default: () => undefined },
});

const emit = defineEmits(['update', 'remove', 'dragStart']);

const { t } = useI18n();
const pipelinesStore = usePipelinesStore();

const isDealTrigger = computed(
  () => props.node.type === 'trigger' && isDealEvent(props.node.data.event)
);
const pipelines = computed(() =>
  [...pipelinesStore.getPipelinesList].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0)
  )
);
const selectedStages = computed(() => {
  const pipeline = pipelines.value.find(
    ({ id }) => id === props.node.data.pipeline_id
  );
  return pipeline?.stages || [];
});
const anyStage = computed(() => props.node.data.any_stage !== false);

const hasError = computed(() => props.errors.length > 0);
const invalid = (key, index = null) =>
  props.errors.includes(index === null ? key : `${key}@${index}`);

const invalidAny = (keys, index = null) =>
  keys.some(key => invalid(key, index));

const FIELD_CLASS =
  '!mb-0 !h-8 w-full !px-2 !py-0 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 outline-none focus:border-n-brand';

const INVALID_CLASS = '!border-n-ruby-9';

const HEADER_STYLES = {
  trigger: 'bg-n-teal-3 text-n-teal-11',
  condition: 'bg-n-amber-3 text-n-amber-11',
  action: 'bg-n-blue-3 text-n-blue-11',
};

const HEADER_ICONS = {
  trigger: 'i-lucide-zap',
  condition: 'i-lucide-git-fork',
  action: 'i-lucide-play',
};

const defaultTitle = computed(
  () =>
    ({
      trigger: t('AUTOMATION.FLOW.TRIGGER'),
      condition: t('AUTOMATION.FLOW.CONDITION'),
      action: t('AUTOMATION.FLOW.ACTION'),
    })[props.node.type]
);
const customLabel = computed(() => props.node.data.label?.trim() || '');
const title = computed(() => customLabel.value || defaultTitle.value);

const actionGroups = computed(() =>
  getActionGroups({ dealEvent: isDealEvent(props.triggerEvent) })
);
const actionSelectGroups = computed(() =>
  actionGroups.value.map(group => ({
    id: group.id,
    label: t(group.labelKey),
    options: group.actions.map(({ key, labelKey, disabled }) => ({
      value: key,
      label: disabled
        ? `${t(labelKey)} (${t('AUTOMATION.FLOW.REQUIRES_CONVERSATION')})`
        : t(labelKey),
      disabled,
      hint: disabled ? t('AUTOMATION.FLOW.NEEDS_CONVERSATION') : '',
    })),
  }))
);

const hasActionBody = computed(
  () =>
    isRecordAction(props.node.data.action) ||
    isPixelAction(props.node.data.action) ||
    isMetaAction(props.node.data.action) ||
    isWaitAction(props.node.data.action)
);

const setAction = value =>
  emit(
    'update',
    props.node.id,
    initialActionData(value, props.node.data.label)
  );
const showActionValue = computed(
  () => props.node.type === 'action' && actionNeedsValue(props.node.data.action)
);

const actionValueOptions = computed(() =>
  props.actionOptions(props.node.data.action)
);

const update = (key, value) =>
  emit('update', props.node.id, { ...props.node.data, [key]: value });

// Mensagem e nota privada têm editor próprio (formatação, variáveis, respostas rápidas).
const isMessageAction = computed(() =>
  ['send_message', 'add_private_note'].includes(props.node.data.action)
);
const showMessageModal = ref(false);
const applyMessage = value => {
  update('value', value);
  showMessageModal.value = false;
};

const isRenaming = ref(false);
const draftLabel = ref('');
const labelInput = ref(null);

const startRename = async () => {
  draftLabel.value = customLabel.value || defaultTitle.value;
  isRenaming.value = true;
  await nextTick();
  labelInput.value?.focus();
  labelInput.value?.select();
};

const commitRename = () => {
  if (!isRenaming.value) return;
  isRenaming.value = false;
  const label = draftLabel.value.trim();
  update('label', label === defaultTitle.value ? '' : label);
};

const cancelRename = () => {
  isRenaming.value = false;
};

const rules = computed(() => props.node.data.rules || []);

const updateRules = next =>
  emit('update', props.node.id, { ...props.node.data, rules: next });

const patchRule = (index, patch) =>
  updateRules(
    rules.value.map((rule, i) => (i === index ? { ...rule, ...patch } : rule))
  );

const ruleField = rule => findField(props.fieldGroups, rule);

const setRuleField = (index, value) => {
  const field = decodeField(value);
  const operators = findField(props.fieldGroups, field)?.operators || [];
  patchRule(index, {
    ...field,
    operator: operators[0]?.value || '',
    value: '',
  });
};

const setRuleOperator = (index, value) =>
  patchRule(index, {
    operator: value,
    ...(operatorNeedsValue(value) ? {} : { value: '' }),
  });

const addRule = () => updateRules([...rules.value, newRule()]);

const removeRule = index =>
  updateRules(rules.value.filter((_, i) => i !== index));

const fieldLabel = field => field.label || t(field.labelKey);

const eventOptions = computed(() =>
  TRIGGER_EVENTS.map(({ key, labelKey }) => ({
    value: key,
    label: t(labelKey),
  }))
);

const pipelineOptions = computed(() =>
  pipelines.value.map(({ id, name }) => ({ value: String(id), label: name }))
);

const fieldOptions = computed(() =>
  props.fieldGroups.flatMap(group =>
    group.fields.map(field => ({
      value: field.id,
      label: `${t(group.labelKey)} / ${fieldLabel(field)}`,
    }))
  )
);

const onInput = key => event => update(key, event.target.value);

const setPipeline = value =>
  emit('update', props.node.id, {
    ...props.node.data,
    pipeline_id: value ? Number(value) : '',
    stage_ids: [],
  });

const setAnyStage = event =>
  emit('update', props.node.id, {
    ...props.node.data,
    any_stage: event.target.checked,
    stage_ids: [],
  });

const toggleStage = stageId => {
  const ids = props.node.data.stage_ids || [];
  emit('update', props.node.id, {
    ...props.node.data,
    stage_ids: ids.includes(stageId)
      ? ids.filter(id => id !== stageId)
      : [...ids, stageId],
  });
};

const bodyStyle = {
  padding: `${BODY_PADDING}px`,
  gap: `${FIELD_GAP}px`,
};
const fieldStyle = { height: `${FIELD_HEIGHT}px` };
const stagesBoxStyle = { height: `${STAGES_BOX_HEIGHT}px` };
const branchStyle = { height: `${BRANCH_HEIGHT}px` };
</script>

<template>
  <div
    class="absolute flex flex-col overflow-visible rounded-xl border bg-n-solid-2 shadow-sm select-none"
    :class="hasError ? 'border-n-ruby-9' : 'border-n-weak'"
    :style="{
      width: `${NODE_WIDTH}px`,
      height: `${nodeHeight(node)}px`,
      left: `${node.x}px`,
      top: `${node.y}px`,
    }"
  >
    <div
      class="group flex items-center gap-2 px-3 text-xs font-medium cursor-grab active:cursor-grabbing rounded-t-xl"
      :class="HEADER_STYLES[node.type]"
      :style="{ height: `${HEADER_HEIGHT}px` }"
      @pointerdown.stop.prevent="emit('dragStart', $event, node.id)"
    >
      <Icon :icon="HEADER_ICONS[node.type]" class="size-4 shrink-0" />
      <input
        v-if="isRenaming"
        ref="labelInput"
        v-model="draftLabel"
        type="text"
        maxlength="40"
        class="!mb-0 !h-6 flex-1 min-w-0 !px-1 !py-0 text-xs font-medium rounded border border-n-brand bg-n-solid-1 text-n-slate-12 outline-none"
        @pointerdown.stop
        @blur="commitRename"
        @keydown.enter.prevent="commitRename"
        @keydown.esc.prevent="cancelRename"
      />
      <template v-else>
        <span
          class="flex-1 truncate"
          :class="{ uppercase: !customLabel }"
          @dblclick.stop="startRename"
        >
          {{ title }}
        </span>
        <button
          type="button"
          class="grid place-items-center size-6 rounded opacity-0 group-hover:opacity-100 hover:bg-n-alpha-2"
          :title="t('AUTOMATION.FLOW.RENAME_NODE')"
          @pointerdown.stop
          @click="startRename"
        >
          <Icon icon="i-lucide-pencil" class="size-3.5" />
        </button>
      </template>
      <button
        v-if="node.type !== 'trigger'"
        type="button"
        class="grid place-items-center size-6 rounded hover:bg-n-alpha-2"
        :title="t('AUTOMATION.FLOW.DELETE_NODE')"
        @pointerdown.stop
        @click="emit('remove', node.id)"
      >
        <Icon icon="i-lucide-x" class="size-3.5" />
      </button>
    </div>

    <div class="flex flex-col" :style="bodyStyle" @pointerdown.stop>
      <template v-if="node.type === 'trigger'">
        <ComboBox
          :has-error="invalid('EVENT_REQUIRED')"
          :model-value="node.data.event"
          :options="eventOptions"
          :placeholder="t('AUTOMATION.FLOW.TRIGGER_PLACEHOLDER')"
          @update:model-value="$event && update('event', $event)"
        />
        <template v-if="isDealTrigger">
          <ComboBox
            :has-error="invalidAny(['PIPELINE_REQUIRED', 'PIPELINE_MISSING'])"
            :model-value="String(node.data.pipeline_id || '')"
            :options="pipelineOptions"
            :placeholder="t('AUTOMATION.FLOW.PIPELINE_PLACEHOLDER')"
            @update:model-value="setPipeline"
          />
          <label
            class="flex items-center gap-2 !mb-0 text-sm cursor-pointer text-n-slate-12"
            :style="fieldStyle"
          >
            <input
              type="checkbox"
              class="!mb-0 !size-4"
              :checked="anyStage"
              @change="setAnyStage"
            />
            {{ t('AUTOMATION.FLOW.ANY_STAGE') }}
          </label>
          <div
            v-if="!anyStage"
            class="flex flex-col overflow-y-auto rounded-lg border"
            :class="
              invalidAny(['STAGES_REQUIRED', 'STAGES_MISSING'])
                ? 'border-n-ruby-9'
                : 'border-n-weak'
            "
            :style="stagesBoxStyle"
          >
            <p
              v-if="!selectedStages.length"
              class="p-2 mb-0 text-xs text-n-slate-11"
            >
              {{ t('AUTOMATION.FLOW.SELECT_PIPELINE_FIRST') }}
            </p>
            <label
              v-for="stage in selectedStages"
              :key="stage.id"
              class="flex items-center gap-2 px-2 !mb-0 text-sm cursor-pointer shrink-0 text-n-slate-12 hover:bg-n-alpha-2"
              :style="fieldStyle"
            >
              <input
                type="checkbox"
                class="!mb-0 !size-4"
                :checked="(node.data.stage_ids || []).includes(stage.id)"
                @change="toggleStage(stage.id)"
              />
              <span class="truncate">{{ stage.name }}</span>
            </label>
          </div>
        </template>
      </template>

      <template v-else-if="node.type === 'condition'">
        <template v-for="(rule, index) in rules" :key="index">
          <div
            v-if="index"
            class="flex items-center justify-center gap-1"
            :style="fieldStyle"
          >
            <button
              v-for="join in ['and', 'or']"
              :key="join"
              type="button"
              class="!px-3 !py-0 h-6 text-xs font-medium uppercase rounded-md border"
              :class="
                rule.join === join
                  ? 'border-n-brand bg-n-brand/10 text-n-brand'
                  : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
              "
              @click="patchRule(index, { join })"
            >
              {{ t(`AUTOMATION.FLOW.JOIN.${join.toUpperCase()}`) }}
            </button>
          </div>
          <div class="flex items-center gap-1" :style="fieldStyle">
            <ComboBox
              :has-error="
                invalidAny(['ATTRIBUTE_REQUIRED', 'ATTRIBUTE_MISSING'], index)
              "
              :model-value="encodeField(rule)"
              :options="fieldOptions"
              :placeholder="t('AUTOMATION.FLOW.SELECT_ATTRIBUTE')"
              @update:model-value="$event && setRuleField(index, $event)"
            />
            <button
              v-if="rules.length > 1"
              type="button"
              class="grid place-items-center shrink-0 !p-0 size-6 rounded hover:bg-n-alpha-2 text-n-slate-11"
              :title="t('AUTOMATION.FLOW.REMOVE_RULE')"
              @click="removeRule(index)"
            >
              <Icon icon="i-lucide-trash-2" class="size-3.5" />
            </button>
          </div>
          <FlowSelect
            :invalid="invalid('OPERATOR_REQUIRED', index)"
            :model-value="rule.operator"
            @update:model-value="setRuleOperator(index, $event)"
          >
            <option value="" disabled>
              {{ t('AUTOMATION.FLOW.SELECT_OPERATOR') }}
            </option>
            <option
              v-for="op in ruleField(rule)?.operators || []"
              :key="op.value"
              :value="op.value"
            >
              {{ t(op.labelKey) }}
            </option>
          </FlowSelect>
          <ComboBox
            v-if="ruleField(rule)?.options?.length"
            :has-error="
              invalidAny(
                ['VALUE_REQUIRED', 'VALUE_MISSING', 'VALUE_INVALID'],
                index
              )
            "
            :model-value="String(rule.value)"
            :disabled="!operatorNeedsValue(rule.operator)"
            :options="ruleField(rule).options"
            :placeholder="t('AUTOMATION.FLOW.VALUE')"
            @update:model-value="patchRule(index, { value: $event })"
          />
          <input
            v-else
            :class="[
              FIELD_CLASS,
              invalidAny(
                ['VALUE_REQUIRED', 'VALUE_MISSING', 'VALUE_INVALID'],
                index
              ) && INVALID_CLASS,
            ]"
            :style="fieldStyle"
            :type="
              { number: 'number', date: 'date' }[ruleField(rule)?.type] ||
              'text'
            "
            :value="rule.value"
            :disabled="!operatorNeedsValue(rule.operator)"
            :placeholder="
              operatorNeedsValue(rule.operator)
                ? t('AUTOMATION.FLOW.VALUE')
                : ''
            "
            @input="patchRule(index, { value: $event.target.value })"
          />
        </template>
        <button
          type="button"
          class="flex items-center justify-center gap-1 !p-0 text-xs font-medium rounded-lg border border-dashed border-n-weak text-n-slate-11 hover:bg-n-alpha-2"
          :style="fieldStyle"
          @click="addRule"
        >
          <Icon icon="i-lucide-plus" class="size-3.5" />
          {{ t('AUTOMATION.FLOW.ADD_RULE') }}
        </button>
      </template>

      <template v-else>
        <FlowGroupedSelect
          :has-error="
            invalid('ACTION_REQUIRED') || invalid('ACTION_NEEDS_CONVERSATION')
          "
          :model-value="node.data.action"
          :groups="actionSelectGroups"
          :placeholder="t('AUTOMATION.FLOW.SELECT_ACTION')"
          @update:model-value="$event !== node.data.action && setAction($event)"
        />
        <FlowActionBody
          v-if="hasActionBody"
          :data="node.data"
          :errors="errors"
          :field-groups="fieldGroups"
          @update="emit('update', node.id, $event)"
        />
        <ComboBox
          v-if="showActionValue && actionValueOptions?.length"
          :has-error="invalid('ACTION_VALUE_REQUIRED')"
          :model-value="String(node.data.value)"
          :options="actionValueOptions"
          :placeholder="t('AUTOMATION.FLOW.ACTION_VALUE')"
          @update:model-value="update('value', $event)"
        />
        <button
          v-else-if="showActionValue && isMessageAction"
          type="button"
          class="flex items-center gap-2 text-left"
          :class="[
            FIELD_CLASS,
            invalid('ACTION_VALUE_REQUIRED') && INVALID_CLASS,
          ]"
          :style="fieldStyle"
          @click="showMessageModal = true"
        >
          <Icon icon="i-lucide-pencil-line" class="size-3.5 shrink-0" />
          <span
            class="truncate"
            :class="node.data.value ? '' : 'text-n-slate-10'"
          >
            {{ node.data.value || t('AUTOMATION.FLOW.MESSAGE.EDIT') }}
          </span>
        </button>
        <input
          v-else-if="showActionValue"
          :class="[
            FIELD_CLASS,
            invalid('ACTION_VALUE_REQUIRED') && INVALID_CLASS,
          ]"
          :style="fieldStyle"
          type="text"
          :value="node.data.value"
          :placeholder="t('AUTOMATION.FLOW.ACTION_VALUE')"
          @input="onInput('value')($event)"
        />
        <MessageEditorModal
          v-if="showMessageModal"
          :value="String(node.data.value || '')"
          :is-private="node.data.action === 'add_private_note'"
          @close="showMessageModal = false"
          @apply="applyMessage"
        />
      </template>
    </div>

    <div
      v-if="node.type === 'condition'"
      class="mt-auto flex flex-col text-xs font-medium"
    >
      <div
        class="flex items-center justify-end px-3 text-n-teal-11"
        :style="branchStyle"
      >
        {{ t('AUTOMATION.FLOW.YES') }}
      </div>
      <div
        class="flex items-center justify-end px-3 text-n-ruby-11"
        :style="branchStyle"
      >
        {{ t('AUTOMATION.FLOW.NO') }}
      </div>
    </div>
  </div>
</template>
